import React, {useRef} from "react";

import {useOnClickOutside} from "../../../hooks/useOnClickOutside";
import {useKeyHandler} from "../../../useEscape";
import {useFocusTrap} from "../../../utils/a11y/useFocusTrap";

type AriaLabel = {"aria-label": string} | {"aria-labelledby": string};
type AriaDescription = {"aria-describedby": string} | {"aria-details": string};
export type AriaDialogProps = AriaLabel & AriaDescription;

type DialogProps = {
  onClose: () => void;
  open: boolean;
  children: React.ReactNode;
} & AriaDialogProps &
  Omit<React.HTMLProps<HTMLDialogElement>, "children" | "open">;

/**
 * A dialog box is basically a popup with no background styling. All content behind the
 * popup will still be visible/unchanged.
 *
 * In most (if not all) use cases, Carbon wants to de-emphasize background content
 * (grey screen, contnet blur, etc) to emphasize what's in the Dialog... so you probably
 * want to use Popup instead of using this component directly.
 */
const Dialog = ({
  onClose,
  open,
  children,
  className = "",
  ...props
}: DialogProps): React.ReactElement => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const setFocusTrap = useFocusTrap(open);

  useOnClickOutside(ref, onClose);
  useKeyHandler("Escape", onClose);

  const classes = `${className} border-none`;

  return (
    <dialog
      ref={el => {
        setFocusTrap(el);
        ref.current = el;
      }}
      open={open}
      className={classes}
      aria-modal
      {...props}
    >
      {children}
    </dialog>
  );
};

export default React.memo(Dialog);
